import { deepClone } from "@mixitone/mvc/src/utils/deepClone";
import {
  type EntryDataField,
  type FormatSchema,
  type SchemaSettings,
  TournamentFormat,
} from "./TournamentFormat";
import {
  type EntryId,
  type Match,
  type TournamentState,
  blankTournamentState,
  matchHasEntries,
  matchLoserId,
  matchWinnerId,
} from "./types";

const schema = {
  numPlayers: {
    type: "number",
    hidden: true,
    description: "Number of players in the tournament",
    minimum: 2,
    maximum: 128,
  },
} satisfies FormatSchema;

/**
 * Single elimination (knockout) tournament format implementation
 */
export class SingleEliminationFormat extends TournamentFormat<typeof schema> {
  getName(): string {
    return "Single Elimination";
  }

  get description(): string {
    return "Traditional knockout tournament";
  }

  getEntryLimits(): { min: number; max: number } {
    return {
      min: 2,
      max: 128,
    };
  }

  getSettingsSchema() {
    return schema;
  }

  getEntryDataSchema(): EntryDataField[] {
    return [];
  }

  getMatchDataSchema(): EntryDataField[] {
    return [];
  }

  transform(state?: TournamentState, settings?: SchemaSettings<typeof schema>): TournamentState {
    // If no state provided, return empty state
    if (!state) {
      const blankState = blankTournamentState();
      blankState.metadata = {
        transformCount: 0,
        lastTransformed: new Date().toISOString(),
        formatVersion: "1.0.0",
      };
      return blankState;
    }

    // Update metadata to track transformations
    const metadata = state.metadata || {};
    metadata.transformCount = (metadata.transformCount || 0) + 1;
    metadata.lastTransformed = new Date().toISOString();
    metadata.formatVersion = "1.0.0"; // Track format version for future compatibility

    // Get entries from round 0
    const { entries } = state;
    const numEntries = Math.max(entries.length, settings?.numPlayers ?? 0);

    // For 0 entries, return empty state
    if (numEntries === 0) {
      return blankTournamentState();
    }

    // Create a new tournament state starting with the entries
    const newState: TournamentState = deepClone(state);
    newState.metadata = metadata;

    // For 1 entry, just return the original state
    if (numEntries === 1) {
      return newState;
    }

    const seedMap = new Map<EntryId, number>();
    entries.forEach((entry, i) => {
      seedMap.set(entry[0], i);
    });

    // Sort entries by seed (ascending)
    const sortedEntries = [...entries].sort((a, b) => a[1] - b[1]);
    const validEntries = new Set(entries.map((entry) => entry[0]));

    // Calculate the number of rounds needed and total slots
    const numRounds = Math.ceil(Math.log2(numEntries));
    const totalSlots = Math.pow(2, numRounds);

    // Calculate the number of byes needed in the first round. The top seeds get byes.
    const numByes = totalSlots - numEntries;
    let previousRoundByes: EntryId[] = [];

    const newRounds = newState.rounds;

    // Create each round
    for (let round = 0; round < numRounds; round++) {
      if (!newRounds[round]) newRounds[round] = [];
      const roundMatches: Match[] = newRounds[round];
      const matchesInRound = Math.pow(2, numRounds - (round + 1)) - (round === 0 ? numByes : 0);

      for (let i = 0; i < matchesInRound; i++) {
        if (roundMatches[i] === undefined) {
          const match: Match = [null, null];
          roundMatches.push(match);
        }
      }

      const entriesInRound = new Set<EntryId>();
      roundMatches.forEach((match) => {
        if (match[0] && validEntries.has(match[0][0])) entriesInRound.add(match[0][0]);
        if (match[1] && validEntries.has(match[1][0])) entriesInRound.add(match[1][0]);
      });

      // For the first round, create matches from the entries
      if (round === 0) {
        // Add first round byes to the set so they get ignored
        for (let i = 0; i < numByes; i++) {
          if (!sortedEntries[i]) continue;

          if (!entriesInRound.has(sortedEntries[i][0])) {
            entriesInRound.add(sortedEntries[i][0]);
            previousRoundByes.push(sortedEntries[i][0]);
          }
        }

        roundMatches.forEach((match, _i) => {
          if (!validEntries.has(match[0]?.[0] ?? "")) {
            const entryId = sortedEntries.find((entry) => !entriesInRound.has(entry[0]));
            if (entryId) {
              match[0] = [entryId[0], false];
              entriesInRound.add(entryId[0]);
            }
          }
          if (!validEntries.has(match[1]?.[0] ?? "")) {
            const entryId = sortedEntries.findLast((entry) => !entriesInRound.has(entry[0]));
            if (entryId) {
              match[1] = [entryId[0], false];
              entriesInRound.add(entryId[0]);
            }
          }
        });
      } else {
        // Create new matches or propagate winners from previous round
        const prevRound = round - 1;
        const prevRoundMatches = newRounds[prevRound];
        // Get previous round winners sorted by seed
        const winners = prevRoundMatches
          .reduce((acc, match) => {
            if (match[0]?.[1]) acc.push(match[0][0]);
            if (match[1]?.[1]) acc.push(match[1][0]);
            return acc;
          }, [] as EntryId[])
          .toSorted((a, b) => seedMap.get(a)! - seedMap.get(b)!);
        const byes = new Set([...previousRoundByes]);
        previousRoundByes = [];

        // Find entries that didn't play in the previous round and are not byes and add them to the byes
        if (round === 1) {
          const lostByes = sortedEntries.filter(
            (entry) =>
              !prevRoundMatches.some((match) => match[0]?.[0] === entry[0] || match[1]?.[0] === entry[0]),
          );
          lostByes.forEach((entry) => {
            byes.add(entry[0]);
          });
        }

        // Place byes first
        for (const bye of byes) {
          if (entriesInRound.has(bye)) continue;

          const match = roundMatches.find((match) => match[0] === null);
          if (match) {
            match[0] = [bye, false];
          } else {
            const match = roundMatches.find((match) => match[1] === null);
            if (match) {
              match[1] = [bye, false];
            } else {
              previousRoundByes.push(bye);
            }
          }
        }

        // Place winners next
        for (const winner of winners) {
          if (entriesInRound.has(winner)) continue;

          const match = roundMatches.find((match) => match[0] === null);
          if (match) {
            match[0] = [winner, false];
          } else {
            const match = roundMatches.find((match) => match[1] === null);
            if (match) {
              match[1] = [winner, false];
            }
          }
        }
      }
    }

    if (!newState.winners || newState.winners.length === 0) {
      // Get the final match from the tournament
      const finalMatch = newRounds[numRounds - 1]?.[0];

      // If final match exists and has valid data
      const hasFinalMatch = finalMatch && matchHasEntries(finalMatch);
      const hasWinner = hasFinalMatch && matchWinnerId(finalMatch);

      if (hasWinner) {
        // Set tournament winners (first and second place)
        const winnerId = matchWinnerId(finalMatch)!;
        const loserId = matchLoserId(finalMatch)!;

        newState.winners = [
          [winnerId, {}], // First place
          [loserId, {}], // Second place
        ];
      }
    }

    return newState;
  }
}
